import moment from "moment";
import Text from "components/general/Text";
import Button from "components/general/Button";
import {
  BOUNDARY_LABELS,
  PunchBoundaryKey,
  ShiftBoundaryDetails,
  ShiftPunch,
} from "apis/types/timekeeper";
import { BOUNDARY_KEYS, DATE_TIME_FORMAT, sourceLabel } from "./helpers";
import styles from "./styles.module.scss";

interface PunchRowProps {
  punch: ShiftPunch;
  shiftId?: number;
  // The recorded boundaries of this shift: they name the punch that closed
  // each side and carry the manual and reused flags the row reads back.
  details: Record<PunchBoundaryKey, ShiftBoundaryDetails | null>;
  canAssignPunches: boolean;
  // `${punchId}-${boundary}` of the assignment currently in flight, if any.
  assigningKey: string | null;
  onAssign: (punchId: number, boundary: PunchBoundaryKey) => void;
}

export default function PunchRow({
  punch,
  shiftId,
  details,
  canAssignPunches,
  assigningKey,
  onAssign,
}: PunchRowProps) {
  // Which punch closed a boundary is settled by the id on the boundary record,
  // not by the assignments list: that list names the shift and the boundary
  // only, so two punches sharing a timestamp would both read as assigned.
  const ownBoundaries = BOUNDARY_KEYS.filter((key) => {
    const boundary = details[key];
    if (!boundary) return false;
    // A boundary that names no punch cannot be matched on id, so the
    // assignments list is the only thing left to go on.
    if (boundary.punch_id == null) {
      return punch.assignments.some(
        (item) => item.shift_id === shiftId && item.boundary === key,
      );
    }
    return boundary.punch_id === punch.id;
  });

  // An adjacent shift has no boundary record here, so those assignments are
  // taken at face value and kept apart from this shift's own boundaries.
  const sharedAssignments = punch.assignments.filter(
    (item) => item.shift_id !== shiftId,
  );

  // Eligibility is the backend's call: the row shows exactly the boundaries
  // this list names and never works one out for itself.
  const availableFor = punch.available_for ?? [];

  const isManual = ownBoundaries.some((key) => details[key]?.manual);
  const isShared =
    !!sharedAssignments.length ||
    ownBoundaries.some((key) => details[key]?.reused);
  // Unused means it closes nothing and can close nothing — a punch is never
  // called missing, that word belongs to the boundary.
  const isUnused =
    !ownBoundaries.length && !sharedAssignments.length && !availableFor.length;

  const timeLabel = moment(punch.sign_in_time).format(DATE_TIME_FORMAT);

  const stateLabels = [
    ...ownBoundaries.map((key) => `Assigned as ${BOUNDARY_LABELS[key]}`),
    ...availableFor.map(
      (boundary) => `Available for ${BOUNDARY_LABELS[boundary] ?? boundary}`,
    ),
    ...(isManual ? ["Manually assigned"] : []),
    ...(isShared ? ["Shared punch"] : []),
    ...(isUnused ? ["Unused"] : []),
  ];

  // One label carries the punch, its state and what can be done with it, so a
  // screen reader reaching the assign button inside is never left guessing.
  const rowLabel = `Punch ${punch.id}, ${timeLabel}, ${sourceLabel(
    punch.source,
  )}. ${stateLabels.join(". ")}`;

  const assignableBoundaries = canAssignPunches ? availableFor : [];

  return (
    <div
      role="group"
      aria-label={rowLabel}
      className={`${styles.punchRow} ${
        ownBoundaries.length ? styles.assigned : ""
      } ${!ownBoundaries.length && availableFor.length ? styles.available : ""}`}
    >
      <div className={styles.punchMain}>
        <Text className={styles.punchDate}>{timeLabel}</Text>
        <Text className={styles.punchTime}>
          {`Punch #${punch.id} · ${sourceLabel(punch.source)}`}
        </Text>

        <div className={styles.badgeGroup}>
          {ownBoundaries.map((key) => (
            <span
              key={`assigned-${key}`}
              className={`${styles.badge} ${styles.assignedBadge}`}
            >
              {`Assigned as ${BOUNDARY_LABELS[key]}`}
            </span>
          ))}
          {availableFor.map((boundary) => (
            <span
              key={`available-${boundary}`}
              className={`${styles.badge} ${styles.availableBadge}`}
            >
              {`Available for ${BOUNDARY_LABELS[boundary] ?? boundary}`}
            </span>
          ))}
          {!!isManual && (
            <span className={`${styles.badge} ${styles.locked}`}>Manual</span>
          )}
          {!!isShared && (
            <span className={`${styles.badge} ${styles.sharedBadge}`}>
              Shared Punch
            </span>
          )}
          {!!isUnused && (
            <span className={`${styles.badge} ${styles.automatic}`}>Unused</span>
          )}
        </div>

        {!!sharedAssignments.length && (
          <Text className={styles.punchTime}>
            {`Also assigned to ${sharedAssignments
              .map(
                (item) =>
                  `shift #${item.shift_id} (${
                    BOUNDARY_LABELS[item.boundary] ?? item.boundary
                  })`,
              )
              .join(", ")}`}
          </Text>
        )}
      </div>

      {!!assignableBoundaries.length && (
        <div className={styles.punchActions}>
          {assignableBoundaries.map((boundary) => (
            <Button
              key={boundary}
              type="button"
              isOutline
              btnClassName={styles.rowBtn}
              ariaLabel={`Assign punch ${punch.id}, ${timeLabel}, as ${
                BOUNDARY_LABELS[boundary] ?? boundary
              }`}
              isLoading={assigningKey === `${punch.id}-${boundary}`}
              // One assignment at a time, so a second click can't race the
              // refetch that the first one triggers.
              disabled={!!assigningKey}
              onClick={() => onAssign(punch.id, boundary)}
            >
              {`Assign as ${BOUNDARY_LABELS[boundary] ?? boundary}`}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}
